import type { MemberAPIResponse } from '@assembly-web/services';
import { TrophyIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import type { z } from 'zod';

import { LoadingSpinner } from '../../../../DesignSystem/Feedback/Icons/LoadingSpinner';
import { TextStyle } from '../../../../DesignSystem/Feedback/TextStyle';
import { Button } from '../../../../DesignSystem/Inputs/Button';
import { MultiStepperModal } from '../../../Shared/Modal/MultiStepper/MultiStepperModal';
import { messages } from './message';
import { getDefaultValues, getSchema, getTabData } from './schema';

export type ChallengeCreationModalProps = {
  handleSubmit: (formFields: unknown) => void;
  isOpen: boolean;
  onCloseModal: (val: boolean) => void;
  userDetails: MemberAPIResponse;
  isSavingChallenge: boolean;
  shareChallengeNode: JSX.Element | null;
};
export function ChallengeCreationModal({
  handleSubmit,
  isOpen,
  onCloseModal,
  userDetails,
  isSavingChallenge,
  shareChallengeNode,
}: ChallengeCreationModalProps) {
  const { formatMessage } = useIntl();

  const tabs = getTabData(formatMessage, userDetails, shareChallengeNode);
  const schema = useMemo(() => {
    return getSchema(formatMessage, userDetails.assembly.currency);
  }, [formatMessage, userDetails.assembly.currency]);

  type FormFields = z.infer<typeof schema>;
  type FieldName = keyof FormFields;

  const methods = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(formatMessage),
  });
  const onSubmit = ({ launchNow }: { launchNow: boolean }) => {
    methods.setValue('launchNow', launchNow);
    if (!isSavingChallenge) handleSubmit({ ...methods.getValues() });
  };
  const customProps = {
    onSubmit,
  };

  const submitFooter = (
    <>
      <Button
        variation="secondaryEmphasized"
        onClick={() =>
          onSubmit({
            launchNow: false,
          })
        }
      >
        {isSavingChallenge ? (
          <div className="flex text-gray-1">
            <LoadingSpinner className="mr-2" />
            {formatMessage(messages.savingLabel)}
          </div>
        ) : (
          formatMessage(messages.saveAndLaunchLater)
        )}
      </Button>

      <Button
        variation="primary"
        onClick={() =>
          onSubmit({
            launchNow: true,
          })
        }
      >
        {isSavingChallenge ? (
          <div className="flex text-gray-1">
            <LoadingSpinner className="mr-2" />
            {formatMessage(messages.savingLabel)}
          </div>
        ) : (
          formatMessage(messages.createAndLaunch)
        )}
      </Button>
    </>
  );

  const handleNextPage = async (selectedTab: string) => {
    // next page click validation
    const selectedPageData = tabs.find((tab) => tab.value === selectedTab);
    const schemaToVerify = selectedPageData?.schema;
    const fields = selectedPageData?.fields;
    const result = schemaToVerify?.safeParse(methods.getValues());
    await methods.trigger(fields as FieldName[]);
    if (result?.success) return true;
    return false;
  };

  const confirmationModal = {
    title: formatMessage(messages.confirmationModalLabel),
    desc: formatMessage(messages.confirmationModalDesc),
    button: formatMessage(messages.confirmationModalButton),
    onClose: () => {
      onCloseModal(false);
    },
  };

  return (
    <FormProvider {...methods} {...customProps}>
      <MultiStepperModal
        tabs={tabs}
        onGetHelpButtonClick={() => {
          window.open(
            'https://www.joinassembly.com/articles/challenges',
            '_blank',
            'noopener,noreferrer'
          );
        }}
        isOpen={isOpen}
        header={
          <div className="flex items-center gap-2 p-2">
            <div className="rounded-full bg-gray-3 p-2">
              <TrophyIcon className="h-6 w-6" />
            </div>
            <TextStyle variant="lg-bold">
              {formatMessage(messages.header)}
            </TextStyle>
          </div>
        }
        customSubmitButton={submitFooter}
        onNextPage={handleNextPage}
        confirmationModal={confirmationModal}
      />
    </FormProvider>
  );
}
